import { z } from 'zod/v4';
import { createTool } from '../../tools';
import { WORKSPACE_TOOLS } from '../constants';
import { extractLinesWithLimit, formatWithLineNumbers } from '../line-utils';
import { emitWorkspaceMetadata, requireFilesystem } from './helpers';
import { applyTokenLimit } from './output-helpers';
import { startWorkspaceSpan } from './tracing';

export const readFileTool = createTool({
  id: WORKSPACE_TOOLS.FILESYSTEM.READ_FILE,
  description:
    'Read the contents of a file from the workspace filesystem. Use offset/limit parameters to read specific line ranges for large files.',
  inputSchema: z.object({
    path: z.string().describe('The path to the file to read (e.g., "/data/config.json")'),
    encoding: z
      .enum(['utf-8', 'utf8', 'base64', 'hex', 'binary'])
      .optional()
      .describe('The encoding to use when reading the file. Defaults to utf-8 for text files.'),
    offset: z
      .number()
      .optional()
      .describe('Line number to start reading from (1-indexed). If omitted, starts from line 1.'),
    limit: z.number().optional().describe('Maximum number of lines to read. If omitted, reads to the end of the file.'),
    showLineNumbers: z
      .boolean()
      .optional()
      .default(true)
      .describe('Whether to prefix each line with its line number (default: true)'),
  }),
  execute: async ({ path, encoding, offset, limit, showLineNumbers }, context) => {
    const { workspace, filesystem } = requireFilesystem(context);
    await emitWorkspaceMetadata(context, WORKSPACE_TOOLS.FILESYSTEM.READ_FILE);

    const span = startWorkspaceSpan(context, workspace, {
      category: 'filesystem',
      operation: 'readFile',
      input: { path, encoding, offset, limit },
      attributes: { filesystemProvider: filesystem.provider },
    });

    try {
      const effectiveEncoding = (encoding as BufferEncoding) ?? 'utf-8';
      const fullContent = await filesystem.readFile(path, { encoding: effectiveEncoding });
      const stat = await filesystem.stat(path);

      const isTextEncoding = !encoding || encoding === 'utf-8' || encoding === 'utf8';

      const tokenLimit = workspace.getToolsConfig()?.[WORKSPACE_TOOLS.FILESYSTEM.READ_FILE]?.maxOutputTokens;

      if (!isTextEncoding) {
        const output = await applyTokenLimit(
          `${stat.path} (${stat.size} bytes, ${effectiveEncoding})\n${fullContent}`,
          tokenLimit,
          'end',
        );
        span.end({ success: true }, { bytesTransferred: stat.size });
        return output;
      }

      if (typeof fullContent !== 'string') {
        const output = await applyTokenLimit(
          `${stat.path} (${stat.size} bytes, base64)\n${fullContent.toString('base64')}`,
          tokenLimit,
          'end',
        );
        span.end({ success: true }, { bytesTransferred: stat.size });
        return output;
      }

      const hasLineRange = offset !== undefined || limit !== undefined;
      const result = extractLinesWithLimit(fullContent, offset, limit);

      const shouldShowLineNumbers = showLineNumbers !== false;
      const formattedContent = shouldShowLineNumbers
        ? formatWithLineNumbers(result.content, result.lines.start)
        : result.content;

      let header: string;
      if (hasLineRange) {
        header = `${stat.path} (lines ${result.lines.start}-${result.lines.end} of ${result.totalLines}, ${stat.size} bytes)`;
      } else {
        header = `${stat.path} (${stat.size} bytes)`;
      }

      const output = await applyTokenLimit(`${header}\n${formattedContent}`, tokenLimit, 'end');
      span.end({ success: true }, { bytesTransferred: stat.size });
      return output;
    } catch (err) {
      span.error(err);
      throw err;
    }
  },
});
